import {
  useCallback, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import {
  Box, Button, ButtonText, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

function siteHostForPermissionHelp(): string {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return 'this site';
}

/**
 * Force-publish the local camera once inside a LiveKit room.
 *
 * Lobby mute choices can leave the camera off; 1:1 conducting and Engage
 * still need a published lecturer feed. Deny must surface Retry — never
 * swallow the camera error (Appear already retries on the wall tile).
 */
export function MeetingPublishCameraGuard({
  children,
  enabled = true,
}: {
  children: ReactNode;
  /** When false, skip the one-time camera publish (e.g. briefing departure). */
  enabled?: boolean;
}) {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const [cameraBlocked, setCameraBlocked] = useState(false);
  const siteHost = useMemo(() => siteHostForPermissionHelp(), []);

  const enableCamera = useCallback(async (): Promise<boolean> => {
    try {
      await localParticipant.setCameraEnabled(true);
      setCameraBlocked(false);
      return true;
    } catch {
      setCameraBlocked(true);
      return false;
    }
  }, [localParticipant]);

  useEffect(() => {
    if (!enabled) return;
    enableCamera();
  }, [enableCamera, enabled]);

  const handleRetry = useCallback(() => {
    enableCamera().catch(() => undefined);
  }, [enableCamera]);

  if (cameraBlocked) {
    return (
      <Box
        flex={1}
        bg="$bgApp"
        p="$4"
        testID="meeting-camera-blocked"
      >
        <VStack space="md" alignItems="center" justifyContent="center" flex={1} maxWidth={480} alignSelf="center">
          <Text size="lg" fontWeight="semibold" color="$statusError" textAlign="center">
            {t('oralExam.cameraDenied')}
          </Text>
          <Text size="sm" color="$textBody" textAlign="center">
            {t('oralExam.mediaBothRequired')}
          </Text>
          <VStack space="sm" width="100%">
            <Text size="sm" color="$textBody" textAlign="center">
              {t('oralExam.mediaBlockedHelpLead', { host: siteHost })}
            </Text>
            <Text size="sm" color="$textSubtle" textAlign="center">
              {t('oralExam.mediaBlockedHelpChrome', { host: siteHost })}
            </Text>
            <Text size="sm" color="$textSubtle" textAlign="center">
              {t('oralExam.mediaBlockedHelpSafari', { host: siteHost })}
            </Text>
          </VStack>
          <Button
            size="sm"
            onPress={handleRetry}
            testID="meeting-media-retry"
          >
            <ButtonText>{t('oralExam.retryCamera')}</ButtonText>
          </Button>
        </VStack>
      </Box>
    );
  }

  return children;
}
